import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard, ActiveEmployeeGuard, InitResolver } from '@pos-common/guards';
import { ROUTE_URLS } from '@pos-common/constants/route-urls.const';
import { PreloadNextModules } from '@pos-common/services/system';
import { StoreModule } from '@ngrx/store';
import { StoreRouterConnectingModule, routerReducer } from '@ngrx/router-store';

const routes: Routes = [
  {
    path: 'login',
    pathMatch: 'full',
    resolve: { init: InitResolver },
    data: {
      preload: true,
    },
    loadChildren: () => import('../pages/login/login.module').then((m) => m.LoginPageModule),
  },
  {
    path: 'employee',
    resolve: { init: InitResolver },
    canActivate: [AuthGuard],
    data: {
      preload: true,
    },
    loadChildren: () => import('../pages/employee-select/employee-select.module').then((m) => m.EmployeeSelectModule),
  },
  {
    path: 'collection',
    children: [
      {
        path: '',
        canActivate: [AuthGuard, ActiveEmployeeGuard],
        data: {
          name: ROUTE_URLS.collection,
          nextPages: [
            ROUTE_URLS.checkout,
            ROUTE_URLS.invoices,
            ROUTE_URLS.customers,
            ROUTE_URLS.report,
            ROUTE_URLS.shifts,
            ROUTE_URLS.settings,
            ROUTE_URLS.calendar,
          ],
        },
        loadChildren: () => import('../pages/collection-view/collection-view.module').then((m) => m.CollectionViewPageModule),
      },
      {
        path: 'checkout',
        canActivate: [AuthGuard, ActiveEmployeeGuard],
        data: {
          name: ROUTE_URLS.checkout,
          nextPages: [ROUTE_URLS.success, ROUTE_URLS.customers],
        },
        loadChildren: () => import('../pages/checkout/checkout.module').then((m) => m.CheckoutPageModule),
      },
    ],
  },
  { path: 'products', redirectTo: 'collection' },
  {
    path: 'customers',
    canActivate: [AuthGuard, ActiveEmployeeGuard],
    data: { name: ROUTE_URLS.customers },
    loadChildren: () => import('../pages/customers2/customers2.module').then((m) => m.CustomersPage2Module),
  },
  {
    path: 'invoices',
    children: [
      {
        path: '',
        canActivate: [AuthGuard, ActiveEmployeeGuard],
        data: {
          name: ROUTE_URLS.invoices,
          nextPages: [
            ROUTE_URLS.invoicesCombine,
            ROUTE_URLS.invoicesOnline,
            ROUTE_URLS.success,
            ROUTE_URLS.collection,
            ROUTE_URLS.settings,
            ROUTE_URLS.customers,
            ROUTE_URLS.shifts,
            ROUTE_URLS.calendar,
          ],
        },
        loadChildren: () => import('../pages/invoices/invoices.module').then((m) => m.InvoicesPageModule),
      },
      {
        path: 'combine',
        canActivate: [AuthGuard, ActiveEmployeeGuard],
        data: { name: ROUTE_URLS.invoicesCombine },
        loadChildren: () => import('../pages/invoices-combine/invoices-combine.module').then((m) => m.InvoicesCombinePageModule),
      },
      {
        path: 'online',
        canActivate: [AuthGuard, ActiveEmployeeGuard],
        data: { name: ROUTE_URLS.invoicesOnline, nextPages: [ROUTE_URLS.collection] },
        loadChildren: () => import('../pages/invoices-online/invoices-online.module').then((m) => m.InvoicesOnlinePageModule),
      },
    ],
  },
  {
    path: 'report',
    canActivate: [AuthGuard, ActiveEmployeeGuard],
    data: { name: ROUTE_URLS.report },
    loadChildren: () => import('../pages/day-report/day-report.module').then((m) => m.DayReportPageModule),
  },
  {
    path: 'shifts',
    canActivate: [AuthGuard, ActiveEmployeeGuard],
    data: { name: ROUTE_URLS.shifts },
    loadChildren: () => import('../pages/shifts/shifts.module').then((m) => m.ShiftsModule),
  },
  {
    path: 'calendar',
    canActivate: [AuthGuard, ActiveEmployeeGuard],
    data: { name: ROUTE_URLS.calendar, nextPages: [ROUTE_URLS.appointment] },
    loadChildren: () => import('../pages/calendar/calendar.module').then((m) => m.CalendarModule),
  },
  {
    path: 'cancellation',
    canActivate: [AuthGuard, ActiveEmployeeGuard],
    loadChildren: () => import('../pages/invoice-cancellation/invoice-cancellation.module').then((m) => m.InvoiceCancellationModule),
  },
  {
    path: 'weak-device',
    data: { name: ROUTE_URLS.weakDevice },
    loadChildren: () => import('../pages/weak-device/weak-device.module').then((m) => m.WeakDeviceModule),
  },
  {
    path: 'success',
    canActivate: [AuthGuard, ActiveEmployeeGuard],
    data: { name: ROUTE_URLS.success, nextPages: [ROUTE_URLS.customers] },
    loadChildren: () => import('../pages/success/success.module').then((m) => m.SuccessPageModule),
  },
  {
    path: 'appointment',
    canActivate: [AuthGuard, ActiveEmployeeGuard],
    data: { name: ROUTE_URLS.appointment, nextPages: [ROUTE_URLS.collection] },
    loadChildren: () => import('../pages/appointment/appointment.module').then((m) => m.AppointmentModule),
  },
  {
    path: 'settings',
    data: {
      name: ROUTE_URLS.settings,
      nextPages: [ROUTE_URLS.printerQueue, ROUTE_URLS.weakDevice],
    },
    loadChildren: () => import('../pages/settings2/settings.module').then((m) => m.SettingsModule),
  },
  { path: '', redirectTo: 'login', pathMatch: 'full' },
  { path: '**', redirectTo: 'employee' },
];

@NgModule({
  imports: [
    StoreModule.forRoot({
      router: routerReducer,
    }),
    RouterModule.forRoot(routes,
      {
        preloadingStrategy: PreloadNextModules,
        useHash: true
      }),
    StoreRouterConnectingModule.forRoot(),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
